import React, { useEffect, useState } from "react";
import { integrationService, driveService } from "../services/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Loader2, Network, Search, HardDrive, RefreshCw, ShieldCheck, Activity, Truck, AlertTriangle } from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";

const REGISTRIES = ["customers", "quotes", "jobs", "assignments", "suppliers", "outreach", "operations", "funding"];

export default function IntegrationConsole() {
  const [enabled, setEnabled] = useState(null);
  const [map, setMap] = useState(null);
  const [activeReg, setActiveReg] = useState("customers");
  const [registry, setRegistry] = useState(null);
  const [regLoading, setRegLoading] = useState(false);
  const [leadId, setLeadId] = useState("");
  const [view360, setView360] = useState(null);
  const [loading360, setLoading360] = useState(false);
  const [error, setError] = useState(null);
  const [nameQuery, setNameQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [driveVerify, setDriveVerify] = useState(null);
  const [driveStats, setDriveStats] = useState(null);
  const [driveIndex, setDriveIndex] = useState(null);
  const [driveDomain, setDriveDomain] = useState("");
  const [driveBusy, setDriveBusy] = useState(false);
  const [driveMsg, setDriveMsg] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [catQuery, setCatQuery] = useState("");
  const [health, setHealth] = useState(null);
  const [healthBusy, setHealthBusy] = useState(false);

  const verifyProviders = async () => {
    setHealthBusy(true);
    try { setHealth(await integrationService.getProviderHealth()); }
    catch (e) { setHealth(null); }
    finally { setHealthBusy(false); }
  };

  const [docusignNotice, setDocusignNotice] = useState(null);
  const connectDocusign = async () => {
    try {
      const { authorize_url } = await integrationService.getDocusignAuthorizeUrl();
      // DocuSign refuses to be embedded (X-Frame-Options: DENY). Open the Authorization Code
      // Grant URL as a top-level navigation in a NEW TAB so it is never loaded inside the
      // preview iframe. NOTE: passing 'noopener' makes window.open() return null per spec, which
      // would falsely trigger the popup-blocked fallback — so we omit it and null the opener on
      // the returned handle instead. Only break out of the frame if the popup is genuinely blocked.
      const w = window.open(authorize_url, "_blank");
      if (w) {
        try { w.opener = null; } catch (_) {}
        setDocusignNotice("DocuSign authorization opened in a new tab — sign in and grant consent there, then return here.");
      } else {
        (window.top || window).location.href = authorize_url;
      }
    } catch (e) {
      setDocusignNotice(e?.response?.data?.detail?.startsWith("docusign_not_configured")
        ? "DocuSign is not configured yet — add the Client ID/Secret to the backend secure env first."
        : "Unable to start DocuSign authorization.");
    }
  };
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("docusign");
    if (p === "connected") { setDocusignNotice("DocuSign Demo connected — verifying…"); }
    else if (p === "error") { setDocusignNotice("DocuSign authorization did not complete. Please retry."); }
  }, []);

  // Driver & Equipment Verification (operates the existing dispatch-eligibility system).
  const [dvDriverId, setDvDriverId] = useState("");
  const [dvElig, setDvElig] = useState(null);
  const [dvBusy, setDvBusy] = useState(false);
  const [dvMsg, setDvMsg] = useState(null);
  const [dvForm, setDvForm] = useState({
    payload_lbs: "", towing_capacity_lbs: "", gvwr_lbs: "",
    driver_qualified: false, authority: false, insurance_active: false,
  });

  const runEligibility = async () => {
    const id = dvDriverId.trim();
    if (!id) return;
    setDvBusy(true); setDvMsg(null); setDvElig(null);
    try { setDvElig(await integrationService.checkDispatchEligibility(id)); }
    catch (e) { setDvMsg(e?.response?.status === 404 ? "Driver not found" : "Unable to check eligibility"); }
    finally { setDvBusy(false); }
  };

  const runVerifyDriver = async () => {
    const id = dvDriverId.trim();
    if (!id) return;
    setDvBusy(true); setDvMsg(null);
    const equipment = {};
    ["payload_lbs", "towing_capacity_lbs", "gvwr_lbs"].forEach((k) => {
      if (dvForm[k] !== "" && dvForm[k] != null) equipment[k] = Number(dvForm[k]);
    });
    // Only send checked flags — an UNCHECKED box means NOT asserted (unknown ⇒ VERIFICATION_REQUIRED),
    // never an explicit admin verification of "false".
    const qualification = {};
    ["driver_qualified", "authority", "insurance_active"].forEach((k) => {
      if (dvForm[k]) qualification[k] = true;
    });
    if (Object.keys(equipment).length === 0 && Object.keys(qualification).length === 0) {
      setDvMsg("Nothing to record — enter at least one verified equipment value or check a verified qualification.");
      setDvBusy(false); return;
    }
    try {
      await integrationService.verifyDriver(id, { equipment, qualification });
      setDvMsg("Verified values recorded (admin-asserted). Re-checking eligibility…");
      setDvElig(await integrationService.checkDispatchEligibility(id));
    } catch (e) {
      setDvMsg(e?.response?.status === 404 ? "Driver not found" : "Unable to record verification");
    } finally { setDvBusy(false); }
  };

  useEffect(() => {
    if (!enabled) return;
    (async () => {
      try { setCatalog(await integrationService.getProviderCatalog()); } catch (e) { setCatalog(null); }
      verifyProviders(); // auto runtime-authoritative health so displayed state is never stale
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    (async () => {
      const on = await integrationService.isEnabled();
      setEnabled(on);
      if (on) {
        try { setMap(await integrationService.getMap()); } catch (e) { /* gated */ }
      }
    })();
  }, []);

  useEffect(() => {
    if (!enabled) return;
    (async () => {
      setRegLoading(true);
      try { setRegistry(await integrationService.getRegistry(activeReg, { limit: 25 })); }
      catch (e) { setRegistry(null); }
      finally { setRegLoading(false); }
    })();
  }, [activeReg, enabled]);

  useEffect(() => {
    if (!enabled) return;
    // Deep-link support: /admin/integration?customer=<leadId> auto-loads the 360 view.
    const params = new URLSearchParams(window.location.search);
    const cid = params.get("customer");
    if (cid) { setLeadId(cid); load360(cid); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const load360 = async (idOverride) => {
    const id = (idOverride ?? leadId).trim();
    if (!id) return;
    setLoading360(true); setError(null); setView360(null);
    try { setView360(await integrationService.getCustomer360(id)); }
    catch (e) { setError(e?.response?.status === 404 ? "Customer not found" : "Unable to load 360 view"); }
    finally { setLoading360(false); }
  };

  const runNameSearch = async () => {
    const q = nameQuery.trim();
    if (!q) { setSearchResults([]); setNoResults(false); return; }
    setSearching(true); setNoResults(false);
    try {
      const r = await integrationService.searchCustomers(q, { limit: 10 });
      const items = r.items || [];
      setSearchResults(items);
      setNoResults(items.length === 0);
    } catch (e) { setSearchResults([]); setNoResults(true); }
    finally { setSearching(false); }
  };

  const pickResult = (item) => {
    setSearchResults([]);
    setNoResults(false);
    setNameQuery(item.label || "");
    setLeadId(item.id);
    load360(item.id);
  };

  useEffect(() => {
    if (!enabled) return;
    (async () => {
      try { setDriveVerify(await driveService.verify()); } catch (e) { /* gated */ }
      try { setDriveStats(await driveService.getStats()); } catch (e) { /* gated */ }
    })();
  }, [enabled]);

  const loadDriveIndex = async (domain) => {
    try { setDriveIndex(await driveService.getIndex({ domain: domain || undefined, limit: 50 })); }
    catch (e) { setDriveIndex(null); }
  };

  const runInventory = async () => {
    setDriveBusy(true); setDriveMsg(null);
    try {
      const r = await driveService.inventory();
      setDriveMsg(r.ok ? `Indexed ${r.counts.total} items (${r.counts.folders} folders, ${r.counts.files} files) • ${r.change_events} change events` : (r.detail || "Inventory failed"));
      setDriveStats(await driveService.getStats());
      await loadDriveIndex(driveDomain);
    } catch (e) { setDriveMsg("Inventory failed"); }
    finally { setDriveBusy(false); }
  };

  const runVerify = async () => {
    setDriveBusy(true);
    try { setDriveVerify(await driveService.verify()); }
    finally { setDriveBusy(false); }
  };

  const [driveAlerts, setDriveAlerts] = useState(null);
  const [insuranceSample, setInsuranceSample] = useState(null);
  const [dupReport, setDupReport] = useState(null);
  const [schedule, setSchedule] = useState(null);

  const runScan = async () => {
    setDriveBusy(true); setDriveMsg(null);
    try {
      const r = await driveService.scan();
      setDriveMsg(r.ok ? `Scan complete • ${r.change_events} change events` : (r.detail || "Scan failed"));
      setDriveAlerts(await driveService.getAlerts({ limit: 50 }));
      setSchedule(await driveService.getSchedule());
    } catch (e) { setDriveMsg("Scan failed"); }
    finally { setDriveBusy(false); }
  };

  const runInsuranceSample = async () => {
    setDriveBusy(true);
    try { setInsuranceSample(await driveService.insuranceExtract("sample")); }
    finally { setDriveBusy(false); }
  };

  const loadDupReport = async () => {
    try {
      setDupReport(await driveService.getDuplicatesReport());
      setDupReview(await driveService.getDuplicatesReview({ limit: 10 }));
    } catch (e) { /* gated */ }
  };

  const [digest, setDigest] = useState(null);
  const [dupReview, setDupReview] = useState(null);
  const loadDigest = async () => {
    try { setDigest(await driveService.getDigest()); } catch (e) { /* gated */ }
  };

  if (enabled === null) {
    return <DashboardLayout><div className="p-8 flex items-center gap-2 text-muted-foreground" data-testid="integration-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…</div></DashboardLayout>;
  }

  if (!enabled) {
    return (
      <DashboardLayout>
      <div className="p-8" data-testid="integration-disabled">
        <Card>
          <CardContent className="py-10 text-center">
            <Network className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
            <p className="text-base font-medium">Internal Integration is not active</p>
            <p className="text-sm text-muted-foreground mt-1">
              Enable <code>relationship_engine_enabled</code> (internal/preview only) to use the Integration Console.
            </p>
          </CardContent>
        </Card>
      </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <div className="p-6 space-y-6" data-testid="integration-console">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Network className="h-6 w-6" /> Integration Console
          <Badge variant="outline" data-testid="integration-readonly-badge">Read-only • Reference-only</Badge>
        </h1>
        {map && (
          <p className="text-sm text-muted-foreground mt-2" data-testid="integration-flow">
            {map.flow.join("  →  ")}
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-1">A read-only view of what's connected, what changed, and what needs attention. Raw identifiers and diagnostics are tucked under “Technical details”.</p>
      </div>

      <Card data-testid="integration-registries">
        <CardHeader><CardTitle className="text-lg">Registries</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {REGISTRIES.map((r) => (
              <Button key={r} size="sm" variant={activeReg === r ? "default" : "outline"}
                      data-testid={`integration-registry-tab-${r}`} onClick={() => setActiveReg(r)}>
                {r}
              </Button>
            ))}
          </div>
          {regLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : registry ? (
            <div data-testid="integration-registry-result">
              <p className="text-sm mb-2">
                <span className="font-medium">{registry.label}</span>{" "}
                <span className="text-muted-foreground">
                  · source: {registry.source_collection || "—"} · count: {registry.count}
                  {registry.supports_manual_entry ? " · manual entry supported" : ""}
                </span>
              </p>
              {registry.count === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="integration-registry-empty">
                  No records yet. {registry.source_collection ? "" : "This registry supports manual entry of real business records."}
                </p>
              ) : (
                <details className="mt-1" data-testid="integration-registry-details">
                  <summary className="text-xs text-muted-foreground cursor-pointer select-none hover:text-foreground">Technical details ({registry.count} records)</summary>
                  <div className="text-xs bg-muted rounded p-3 overflow-auto max-h-64 mt-2" data-testid="integration-registry-items">
                    {registry.items.map((it, i) => (
                      <div key={i} className="py-1 border-b border-border/40 last:border-0">
                        {Object.entries(it).map(([k, v]) => (
                          <span key={k} className="mr-3"><span className="text-muted-foreground">{k}:</span> {String(typeof v === "object" ? JSON.stringify(v) : v)}</span>
                        ))}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Unable to load registry.</p>
          )}
        </CardContent>
      </Card>

      <Card data-testid="integration-360">
        <CardHeader><CardTitle className="text-lg">Customer 360 (reference view)</CardTitle></CardHeader>
        <CardContent>
          <div className="mb-4">
            <p className="text-xs font-medium text-muted-foreground mb-1">Search by name / email / phone</p>
            <div className="flex gap-2 max-w-md relative">
              <Input placeholder="e.g. Christie Meyers" value={nameQuery}
                     onChange={(e) => { setNameQuery(e.target.value); setNoResults(false); setSearchResults([]); }}
                     onKeyDown={(e) => { if (e.key === "Enter") runNameSearch(); }}
                     data-testid="integration-360-search-input" />
              <Button variant="outline" onClick={runNameSearch} data-testid="integration-360-search-btn">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
              {searchResults.length > 0 && (
                <div className="absolute top-11 left-0 z-10 w-full bg-background border border-border rounded shadow-md max-h-56 overflow-auto"
                     data-testid="integration-360-search-results">
                  {searchResults.map((it) => (
                    <button key={it.id} onClick={() => pickResult(it)}
                            className="block w-full text-left px-3 py-2 text-sm hover:bg-muted border-b border-border/40 last:border-0"
                            data-testid={`integration-360-search-result-${it.id}`}>
                      <span className="font-medium">{it.label}</span>
                      {it.snippet ? <span className="text-muted-foreground ml-2 text-xs">{it.snippet}</span> : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {!searching && noResults && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="integration-360-search-empty">No matching customers.</p>
            )}
          </div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Or open by customer (lead) id</p>
          <div className="flex gap-2 max-w-md">
            <Input placeholder="Enter customer (lead) id" value={leadId}
                   onChange={(e) => setLeadId(e.target.value)} data-testid="integration-360-input" />
            <Button onClick={() => load360()} data-testid="integration-360-load">
              <Search className="h-4 w-4 mr-1" /> Load
            </Button>
          </div>
          {loading360 && <div className="mt-3 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
          {error && <p className="mt-3 text-sm text-red-600" data-testid="integration-360-error">{error}</p>}
          {view360 && (
            <div className="mt-4 space-y-4" data-testid="integration-360-result">
              <div>
                <p className="text-sm font-medium">{view360.customer.company_name || view360.customer.contact_name || view360.customer.full_name || view360.customer.id}</p>
                <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
                  {view360.customer.city && <span data-testid="integration-360-location">{view360.customer.city}{view360.customer.state ? `, ${view360.customer.state}` : ""}</span>}
                  {view360.customer.status && <span data-testid="integration-360-status">status: {view360.customer.status}</span>}
                  {view360.customer.provenance && <Badge variant="outline" data-testid="integration-360-provenance">provenance: {view360.customer.provenance}</Badge>}
                </div>
              </div>
              {(() => {
                const c = view360.chain || {};
                const sections = [
                  { key: 'quotes', label: 'Quotes', rows: c.quotes, empty: 'No quotes on record.' },
                  { key: 'jobs', label: 'Jobs / Operational History', rows: c.jobs, empty: 'No jobs or loads on record.' },
                  { key: 'assignments', label: 'Dispatch Assignments', rows: c.assignments, empty: 'Relationship not yet connected — no dispatch assignments.' },
                  { key: 'completions', label: 'Job Completion / POD', rows: c.completions, empty: 'Not yet connected — no completion/POD recorded.' },
                  { key: 'invoices', label: 'Customer Invoices / AR', rows: c.invoices, empty: 'Not yet connected — no customer invoice issued.' },
                  { key: 'payments', label: 'Customer Payments', rows: c.payments, empty: 'Not yet connected — no customer payment recorded.' },
                  { key: 'documents', label: 'Documents (references only)', rows: c.documents, empty: 'No linked documents.' },
                  { key: 'operations_timeline', label: 'Activity Timeline', rows: view360.operations_timeline, empty: 'No recorded activity yet.' },
                ];
                const LC_TONE = {
                  CONNECTED: 'bg-green-100 text-green-700 border-green-200',
                  NOT_YET_CONNECTED: 'bg-amber-100 text-amber-700 border-amber-200',
                  NO_CANONICAL_MODEL: 'bg-gray-100 text-gray-600 border-gray-200',
                  NOT_APPLICABLE: 'bg-gray-100 text-gray-500 border-gray-200',
                  INTERNAL_ONLY: 'bg-blue-100 text-blue-700 border-blue-200',
                };
                const LC_ORDER = ['lead', 'quote', 'job', 'dispatch', 'tracking', 'completion', 'invoice', 'payment', 'settlement', 'profitability'];
                const lc = view360.lifecycle_status || {};
                return (
                  <>
                    <div className="flex flex-wrap gap-2 text-xs" data-testid="integration-360-counts">
                      {Object.entries(view360.counts || {}).map(([k, v]) => (
                        <Badge key={k} variant="secondary" data-testid={`integration-360-count-${k}`}>{k}: {v}</Badge>
                      ))}
                    </div>
                    {sections.map((s) => (
                      <div key={s.key} data-testid={`integration-360-${s.key}`}>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{s.label}</p>
                        {Array.isArray(s.rows) && s.rows.length > 0 ? (
                          <div className="text-xs bg-muted rounded p-3 space-y-1 overflow-auto max-h-48">
                            {s.rows.map((r, i) => (
                              <div key={i} className="py-1 border-b border-border/40 last:border-0" data-testid={`integration-360-${s.key}-${i}`}>
                                {Object.entries(r).map(([k, v]) => (
                                  <span key={k} className="mr-3"><span className="text-muted-foreground">{k}:</span> {String(typeof v === 'object' ? JSON.stringify(v) : v)}</span>
                                ))}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic" data-testid={`integration-360-${s.key}-empty`}>{s.empty}</p>
                        )}
                      </div>
                    ))}

                    {/* Honest operational lifecycle + financial-source status */}
                    <div data-testid="integration-360-lifecycle">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Operational Lifecycle Status</p>
                      <div className="text-xs bg-muted rounded p-3 space-y-1.5">
                        {LC_ORDER.filter((k) => lc[k]).map((k) => (
                          <div key={k} className="flex items-start gap-2" data-testid={`integration-360-lifecycle-${k}`}>
                            <Badge variant="outline" className={`${LC_TONE[lc[k].state] || ''} shrink-0`} data-testid={`integration-360-lifecycle-${k}-state`}>
                              {k}: {String(lc[k].state).replace(/_/g, ' ').toLowerCase()}
                            </Badge>
                            {lc[k].note && <span className="text-muted-foreground">{lc[k].note}</span>}
                            {lc[k].count != null && !lc[k].note && <span className="text-muted-foreground">count: {lc[k].count}</span>}
                          </div>
                        ))}
                      </div>
                      {view360.financials?.note && (
                        <p className="text-[11px] text-muted-foreground italic mt-1" data-testid="integration-360-financials-note">{view360.financials.note}</p>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="integration-drive-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <HardDrive className="h-5 w-5" /> Google Drive — Master Knowledge Index
            <Badge variant="outline" data-testid="integration-drive-scope">read-only • metadata.readonly</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={runVerify} disabled={driveBusy} data-testid="drive-verify-btn">
              <ShieldCheck className="h-4 w-4 mr-1" /> Verify access
            </Button>
            <Button size="sm" onClick={runInventory} disabled={driveBusy} data-testid="drive-inventory-btn">
              {driveBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Run inventory
            </Button>
            {driveVerify && (
              <Badge variant={driveVerify.ok ? "secondary" : "destructive"} data-testid="drive-verify-status">
                {driveVerify.ok ? `verified • ${driveVerify.root_folder?.name}` : `not verified • ${driveVerify.step || ""}`}
              </Badge>
            )}
          </div>
          {driveMsg && <p className="text-xs text-muted-foreground" data-testid="drive-inventory-msg">{driveMsg}</p>}

          {driveStats && driveStats.total > 0 && (
            <div data-testid="drive-stats">
              <div className="flex flex-wrap gap-2 text-xs mb-2">
                <Badge variant="secondary" data-testid="drive-stat-total">total: {driveStats.total}</Badge>
                <Badge variant="secondary" data-testid="drive-stat-linked">linked to platform: {driveStats.linked_to_platform}</Badge>
              </div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">By operational domain</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(driveStats.by_domain).sort((a, b) => b[1] - a[1]).map(([d, n]) => (
                  <button key={d} onClick={() => { setDriveDomain(d); loadDriveIndex(d); }}
                          className={`text-xs px-2 py-1 rounded border transition-colors ${driveDomain === d ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                          data-testid={`drive-domain-${d.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                    {d}: {n}
                  </button>
                ))}
              </div>
            </div>
          )}

          {driveIndex && driveIndex.items?.length > 0 && (
            <div data-testid="drive-index-list">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                {driveDomain || "All"} — {driveIndex.total} document(s)
              </p>
              <div className="text-xs bg-muted rounded p-3 space-y-1 overflow-auto max-h-72">
                {driveIndex.items.map((it) => (
                  <div key={it.id} className="py-1 border-b border-border/40 last:border-0" data-testid={`drive-doc-${it.id}`}>
                    <span className="font-medium">{it.document_title}</span>
                    <span className="text-muted-foreground ml-2">[{it.document_type}]</span>
                    {it.bla_code && <Badge variant="outline" className="ml-2">{it.bla_code}</Badge>}
                    {it.platform_relationships?.length > 0 && (
                      <Badge variant="secondary" className="ml-2" data-testid={`drive-doc-linked-${it.id}`}>
                        linked: {it.platform_relationships.map((r) => r.label || r.entity_id).join(", ")}
                      </Badge>
                    )}
                    <div className="text-muted-foreground truncate">{it.folder_location}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-border/50 pt-4 mt-2" data-testid="drive-monitoring-section">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Monitoring & Insurance Intelligence
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={runScan} disabled={driveBusy} data-testid="drive-scan-btn">
                <RefreshCw className="h-4 w-4 mr-1" /> Run scan (alerts)
              </Button>
              <Button variant="outline" size="sm" onClick={runInsuranceSample} disabled={driveBusy} data-testid="drive-insurance-sample-btn">
                Insurance sample
              </Button>
              <Button variant="outline" size="sm" onClick={loadDupReport} disabled={driveBusy} data-testid="drive-duplicates-report-btn">
                Duplicate report
              </Button>
              <Button variant="outline" size="sm" onClick={loadDigest} disabled={driveBusy} data-testid="drive-digest-btn">
                Alerts digest
              </Button>
            </div>

            {schedule && (
              <p className="text-xs text-muted-foreground mt-2" data-testid="drive-schedule-status">
                Scheduled scans: <span className="font-medium">{schedule.status}</span> • cadence: {schedule.cadence || schedule.proposed_cadence}
              </p>
            )}
            {driveAlerts && (
              <div className="mt-2" data-testid="drive-alerts">
                <p className="text-xs font-medium">Alerts: {driveAlerts.count}</p>
                <div className="text-xs bg-muted rounded p-2 mt-1 space-y-1 max-h-32 overflow-auto">
                  {driveAlerts.alerts.slice(0, 8).map((a, i) => (
                    <div key={i} data-testid={`drive-alert-${i}`}>
                      <Badge variant={a.severity === "high" ? "destructive" : "secondary"} className="mr-2">{a.severity}</Badge>
                      {a.alert_type} — {a.source_path || a.source_file_id}
                    </div>
                  ))}
                  {driveAlerts.count === 0 && <span className="text-muted-foreground">No open alerts.</span>}
                </div>
              </div>
            )}
            {insuranceSample && (
              <div className="mt-2 text-xs" data-testid="drive-insurance-result">
                <p className="font-medium">Insurance sample — tested {insuranceSample.tested} ({(insuranceSample.file_types_tested || []).join(", ")}), policy-like: {insuranceSample.policy_like_documents}, passed: {String(insuranceSample.sample_threshold_passed)}</p>
                <p className="text-muted-foreground mt-1">{insuranceSample.recommendation}</p>
              </div>
            )}
            {dupReport && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="drive-dup-report">
                Exact-duplicate groups: <span className="font-medium">{dupReport.exact_duplicate_group_count}</span> • near: {dupReport.near_duplicate_group_count} • superseded: {dupReport.superseded_version_group_count} — recommendations only (per-group Founder approval required)
              </p>
            )}
            {dupReview && dupReview.lowest_risk_groups?.length > 0 && (
              <div className="mt-2" data-testid="drive-dup-review">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  {dupReview.returned} lowest-risk exact-duplicate groups — review only (per-group Founder approval required)
                </p>
                <div className="space-y-2 max-h-96 overflow-auto">
                  {dupReview.lowest_risk_groups.map((g, i) => {
                    const fmtSize = (b) => (b == null ? "—" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);
                    return (
                      <div key={i} className="text-xs bg-muted rounded p-2 space-y-1" data-testid={`drive-dup-group-${i}`}>
                        <div className="flex items-center flex-wrap gap-2">
                          <Badge variant={g.risk_level === "low" ? "secondary" : "destructive"}>{g.risk_level} risk</Badge>
                          <span className="font-mono text-[11px] text-muted-foreground" data-testid={`drive-dup-group-id-${i}`}>Group {g.group_fingerprint}</span>
                          <span className="text-muted-foreground">×{g.item_count} copies</span>
                          <Badge variant="outline" data-testid={`drive-dup-status-${i}`}>{g.founder_approval_status}</Badge>
                        </div>
                        <p className="text-muted-foreground">Match evidence: {g.match_evidence}</p>
                        <p className="text-muted-foreground">Proposed authoritative: <span className="font-medium text-foreground">{g.proposed_authoritative?.file_name}</span> — {g.why_retain_proposed}</p>
                        <div className="divide-y divide-border/40 border border-border/40 rounded">
                          {(g.items || []).map((it, j) => (
                            <div key={j} className="p-1.5" data-testid={`drive-dup-group-${i}-item-${j}`}>
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium truncate">{it.file_name}</span>
                                <span className="text-muted-foreground whitespace-nowrap">{it.file_type} • {fmtSize(it.file_size_bytes)}</span>
                              </div>
                              <p className="text-[10px] text-muted-foreground truncate">Path: {it.folder_location}</p>
                              <p className="text-[10px] text-muted-foreground">ID: {it.file_id} • created {(it.created_time || "").slice(0, 10)} • modified {(it.modified_time || "").slice(0, 10)}</p>
                              <p className="text-[10px] text-muted-foreground">Relationships: {(it.platform_relationships || []).length ? `${it.platform_relationships.length} linked` : "none"}</p>
                              <p className="text-[10px]">Consequence: {it.consequence_if_removed}</p>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground italic">{g.recommended_action}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {digest && (
              <div className="mt-2" data-testid="drive-digest">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  Alerts digest — {digest.actionable_count} actionable • {digest.high_priority_unresolved} high-priority
                </p>
                {digest.message ? (
                  <p className="text-xs text-muted-foreground" data-testid="drive-digest-empty">{digest.message}</p>
                ) : (
                  <div className="text-xs bg-muted rounded p-2 space-y-1 max-h-40 overflow-auto">
                    {digest.categories.map((c, i) => (
                      <div key={i} data-testid={`drive-digest-cat-${i}`}>
                        <span className="font-medium">{c.category}</span> <Badge variant="outline" className="ml-1">{c.count}</Badge>
                        {c.items.slice(0, 3).map((it, j) => (
                          <div key={j} className="text-muted-foreground truncate ml-2">• {it.severity} — {it.source_path || it.source_file_id}</div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {catalog && (
        <Card data-testid="provider-catalog">
          <CardHeader>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="flex items-center gap-2 text-base"><Network className="w-4 h-4" /> Integration Provider Control Surface</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">{catalog.discovery_note}</p>
              </div>
              <Button size="sm" onClick={verifyProviders} disabled={healthBusy} data-testid="provider-verify-btn">
                {healthBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Activity className="h-4 w-4 mr-1" />}
                Verify connections
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {docusignNotice && (
              <div className="text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-800 p-2" data-testid="docusign-notice">
                {docusignNotice}
              </div>
            )}
            {/* Runtime-authoritative summary + Founder Action Queue */}
            {health && (
              <div className="space-y-3" data-testid="provider-health-summary">
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge className="bg-green-100 text-green-700 border-green-200" data-testid="provider-health-connected">Connected (verified): {health.counts?.connected ?? 0}</Badge>
                  {Object.entries(health.counts?.by_health || {}).map(([k, v]) => (
                    <Badge key={k} variant="secondary">{(health.health_legend?.[k] || k)}: {v}</Badge>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground italic">{health.governance_note}</p>
                {health.founder_action_queue?.length > 0 && (
                  <div data-testid="provider-founder-action-queue">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> Founder Action Queue (activation-value order)
                    </p>
                    <div className="space-y-1">
                      {health.founder_action_queue.map((q, i) => (
                        <div key={i} className="text-xs bg-muted rounded p-2 flex items-start gap-2" data-testid={`provider-action-${q.key}`}>
                          <Badge variant="outline" className="shrink-0">{q.priority}</Badge>
                          <div>
                            <span className="font-medium">{q.provider}</span>
                            <Badge variant="secondary" className="ml-2">{health.status_legend?.[q.verified_connection_status] || q.verified_connection_status}</Badge>
                            <p className="text-muted-foreground mt-0.5">{q.blocker || q.founder_action}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 text-xs" data-testid="provider-catalog-status-counts">
              <span className="text-muted-foreground">{health ? 'Live connection state shown below (catalog defaults hidden).' : 'Catalog defaults (pre-verification):'}</span>
              {!health && Object.entries(catalog.status_counts || {}).map(([k, v]) => (
                <Badge key={k} variant="secondary">{(catalog.status_legend?.[k] || k)}: {v}</Badge>
              ))}
            </div>
            <Input data-testid="provider-catalog-search" placeholder="Search provider metadata (name / category / purpose)…" value={catQuery}
              onChange={async (e) => { const v = e.target.value; setCatQuery(v); try { setCatalog(await integrationService.getProviderCatalog(v ? { q: v } : {})); } catch (_) {} }} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(catalog.providers || []).map((p) => {
                const h = (health?.providers || []).find((x) => x.key === p.key);
                const status = h ? h.verified_connection_status : p.status;
                const statusLabel = (health?.status_legend?.[status]) || (catalog.status_legend?.[status]) || status;
                const tone = status === 'CONNECTED_READ_ONLY' || status === 'CONNECTED_TEST_MODE' || status === 'CONNECTED' || status === 'VERIFIED_READ_ONLY'
                  ? 'bg-green-100 text-green-700 border-green-200'
                  : status === 'EXTERNAL_DEPENDENCY' || status === 'CREDENTIALS_REQUIRED'
                  ? 'bg-amber-100 text-amber-700 border-amber-200'
                  : status === 'FAILED'
                  ? 'bg-red-100 text-red-700 border-red-200'
                  : 'bg-gray-100 text-gray-600 border-gray-200';
                return (
                  <div key={p.key} data-testid={`provider-${p.key}`} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-sm">{p.name}</div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant="outline" className={`${tone} whitespace-normal break-words text-right leading-tight max-w-[150px]`} data-testid={`provider-${p.key}-status`}>{statusLabel}</Badge>
                        {p.priority && <Badge variant="outline" className="text-[10px]">{p.priority}</Badge>}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">{p.category} • {p.connection_method} • {p.read_write}</p>
                    <p className="text-xs">{p.purpose}</p>
                    {h && (
                      <div className="text-[11px] bg-muted/60 rounded p-2 space-y-0.5 mt-1" data-testid={`provider-${p.key}-health`}>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                          <span><span className="text-muted-foreground">Health:</span> {health.health_legend?.[h.health] || h.health}</span>
                          <span><span className="text-muted-foreground">Env:</span> {h.environment}</span>
                          <span><span className="text-muted-foreground">Execution:</span> {h.execution_state}</span>
                          <span><span className="text-muted-foreground">Search:</span> {h.search_capability}</span>
                        </div>
                        <div><span className="text-muted-foreground">Account:</span> {h.account_status}</div>
                        {h.last_verified && <div><span className="text-muted-foreground">Last verified:</span> {String(h.last_verified).slice(0, 19)}Z</div>}
                        {h.detail && <div className="text-muted-foreground">{h.detail}</div>}
                        {h.blocker && <div className="text-amber-700"><span className="font-medium">Blocker:</span> {h.blocker}</div>}
                      </div>
                    )}
                    {p.key === 'esign' && (h ? h.verified_connection_status !== 'CONNECTED_TEST_MODE' : true) && (
                      <Button size="sm" variant="outline" className="mt-1" onClick={connectDocusign} data-testid="docusign-connect-btn">
                        <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Connect DocuSign (Demo)
                      </Button>
                    )}
                    <p className="text-[11px] text-muted-foreground"><span className="font-medium">Founder action:</span> {p.founder_action}</p>
                    <p className="text-[11px] text-muted-foreground">Modules: {(p.bluelock_modules || []).join(", ")}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dispatch Readiness — Driver & Equipment Verification (operates existing eligibility engine) */}
      <Card data-testid="dispatch-verification-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Truck className="w-4 h-4" /> Dispatch Readiness — Driver & Equipment Verification</CardTitle>
          <p className="text-xs text-muted-foreground">
            Record ADMIN-VERIFIED equipment/qualification and check deterministic dispatch eligibility.
            Applicant-stated ≠ verified · payload ≠ towing · GVWR ≠ payload · approved ≠ dispatch-eligible.
            Unknowns ⇒ VERIFICATION_REQUIRED. No fabricated values; no dispatch executed.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2 max-w-md">
            <Input placeholder="Enter driver (user) id" value={dvDriverId}
                   onChange={(e) => setDvDriverId(e.target.value)} data-testid="dv-driver-id-input" />
            <Button variant="outline" onClick={runEligibility} disabled={dvBusy} data-testid="dv-check-eligibility-btn">
              {dvBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />} Check eligibility
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {[["payload_lbs", "Payload (lbs)"], ["towing_capacity_lbs", "Towing capacity (lbs)"], ["gvwr_lbs", "GVWR (lbs)"]].map(([k, label]) => (
              <div key={k}>
                <label className="text-muted-foreground">{label}</label>
                <Input type="number" value={dvForm[k]} data-testid={`dv-${k}`}
                       onChange={(e) => setDvForm({ ...dvForm, [k]: e.target.value })} />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 text-xs">
            {[["driver_qualified", "Driver qualified"], ["authority", "Operating authority"], ["insurance_active", "Insurance active"]].map(([k, label]) => (
              <label key={k} className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={!!dvForm[k]} data-testid={`dv-${k}`}
                       onChange={(e) => setDvForm({ ...dvForm, [k]: e.target.checked })} />
                {label}
              </label>
            ))}
          </div>
          <Button size="sm" onClick={runVerifyDriver} disabled={dvBusy} data-testid="dv-record-verification-btn">
            Record verified values
          </Button>
          {dvMsg && <p className="text-xs text-muted-foreground" data-testid="dv-msg">{dvMsg}</p>}

          {dvElig && (
            <div className="text-xs bg-muted rounded p-3 space-y-1" data-testid="dv-eligibility-result">
              <div className="flex items-center gap-2">
                <Badge className={dvElig.verdict === 'OK' ? 'bg-green-100 text-green-700 border-green-200' : dvElig.verdict === 'BLOCKED' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-amber-100 text-amber-700 border-amber-200'} data-testid="dv-verdict">
                  {dvElig.verdict}
                </Badge>
                <span className="text-muted-foreground">
                  {dvElig.driver_found === false
                    ? "Driver not found."
                    : `approved: ${dvElig.driver_approved} · verification recorded: ${dvElig.verification_recorded} · complete: ${dvElig.verification_complete}`}
                </span>
              </div>
              <div className="space-y-0.5">
                {(dvElig.checks || []).map((c, i) => (
                  <div key={i} data-testid={`dv-check-${i}`} className="flex gap-2">
                    <Badge variant="outline" className={`shrink-0 ${c.status === 'OK' ? 'text-green-700' : c.status === 'BLOCKED' ? 'text-red-700' : 'text-amber-700'}`}>{c.status}</Badge>
                    <span className="text-muted-foreground"><span className="font-medium text-foreground">{c.name}:</span> {c.detail}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground italic">{dvElig.note}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
}
